import economyRegions from "@/config/economy-regions";
import economies from "@/config/economies";
import regions from "@/config/regions";
import sectors from "@/config/sectors";
import type { Item } from "@/components/Select";
import { getKeys } from "./keys";

type ItemWithoutLabel = { value: string; items?: ItemWithoutLabel[] };
type ReadOnlyItemWithoutLabel = {
  value: string;
  items?: readonly ReadOnlyItemWithoutLabel[];
};

const defaultSort = (a: Item, b: Item) => {
  if (!a.label || !b.label) {
    return 0;
  }

  if (a.label.toLowerCase() === "other" || a.label.toLowerCase() === "others") {
    return 1;
  }
  if (b.label.toLowerCase() === "other" || b.label.toLowerCase() === "others") {
    return -1;
  }

  return a.label.localeCompare(b.label);
};

const filterItems = (
  items: readonly ReadOnlyItemWithoutLabel[],
  options: readonly string[]
) => {
  let filteredItems = [...items];
  items.forEach((item) => {
    const index = filteredItems.findIndex(
      (i) => i.value.toLowerCase() === item.value.toLowerCase()
    );
    if (item.items) {
      filteredItems[index] = {
        ...item,
        items: filterItems(item.items, options),
      };
    }

    if (
      !filteredItems[index].items?.length &&
      !options.some((o) => o.toLowerCase() === item.value.toLowerCase())
    ) {
      filteredItems = filteredItems.filter((i) => i.value !== item.value);
    }
  });

  return filteredItems;
};

const formatItems = (
  items: readonly ReadOnlyItemWithoutLabel[],
  options: readonly string[],
  labels: Record<string, string>,
  sort: (a: Item, b: Item) => number = defaultSort
) => {
  return items
    .reduce<Item[]>((formattedItems, item) => {
      if (options.some((o) => o.toLowerCase() === item.value.toLowerCase())) {
        formattedItems.push({
          value: item.value.toLowerCase(),
          label: labels[item.value],
          ...(item.items?.length
            ? { items: formatItems(item.items, options, labels, sort) }
            : {}),
        });
      } else if (item.items?.length) {
        formattedItems.push({
          value: item.value.toLowerCase(),
          isGroupLabel: true,
          label: labels[item.value],
          items: formatItems(item.items, options, labels, sort),
        });
      }

      return formattedItems;
    }, [])
    .sort(sort);
};

const sectorHierarchy = [
  {
    value: "0AG",
    items: [
      {
        value: "0MG",
        items: [
          { value: "01M" },
          { value: "02M" },
          { value: "03M" },
          { value: "04M" },
          { value: "05M" },
          { value: "06M" },
          { value: "07M" },
          { value: "09M" },
          { value: "10M" },
          { value: "11M" },
          { value: "12M" },
          { value: "13M" },
        ],
      },
      {
        value: "0RS",
        items: [{ value: "01R" }, { value: "02R" }, { value: "03R" }],
      },
      {
        value: "08M",
      },
    ],
  },
];

export function getSectorItems(options: readonly string[]) {
  const filteredItems = filterItems(sectorHierarchy, options);
  return formatItems(filteredItems, options, sectors);
}

const regionCodes = Array.from(new Set(Object.values(economyRegions)));
const regionOrder = [
  "APC",
  "GCH",
  "NAC",
  "LAC",
  "EUE",
  "ECA",
  "MNA",
  "SSA",
  "ROW",
];
const regionSort = (a: Item, b: Item) => {
  if (!a.value || !b.value) {
    return defaultSort(a, b);
  }

  const aIndex = regionOrder.indexOf(a.value.toUpperCase());
  const bIndex = regionOrder.indexOf(b.value.toUpperCase());
  if (aIndex === -1 && bIndex === -1) {
    return defaultSort(a, b);
  }
  if (aIndex === -1) {
    return 1;
  }
  if (bIndex === -1) {
    return -1;
  }

  return aIndex - bIndex;
};

const economyRegionsHierarchy = regionCodes.reduce<ItemWithoutLabel[]>(
  (acc, region) => {
    acc.push({
      value: region,
      items: getKeys(economyRegions)
        .filter((r) => economyRegions[r] === region)
        .map((r) => ({ value: r })),
    });
    return acc;
  },
  []
);

const economyRegionsHierarchyWithWld = [
  {
    value: "WLD",
    items: economyRegionsHierarchy,
  },
];

const economyLabels = {
  ...economies,
  ...regions,
};

export function getEconomyItems(options: readonly string[]) {
  const filteredItems = filterItems(
    options.some((o) => o.toLowerCase() === "wld")
      ? economyRegionsHierarchyWithWld
      : economyRegionsHierarchy,
    options
  );
  return formatItems(filteredItems, options, economyLabels, regionSort);
}
